import { useEffect, useState } from "react";
import { Box, CircularProgress } from "@mui/material";
import { SxProps, Theme } from "@mui/material/styles";
import GavelOutlinedIcon from "@mui/icons-material/GavelOutlined";
import { formatUnits } from "ethers";
import { useNavigate } from "react-router-dom";

import { AppRoutes } from "../../config/app.routes";

import ProgressStepper from "../ProgressStepper";
import AnchorDeedDialog from "./AnchorDeedDialog";
import JudgeChallengeDialog from "./JudgeChallengeDialog";
import useSettlement, { Settlement } from "../../hooks/useSettlement";
import useRelayCapabilities from "../../hooks/useRelayCapabilities";
import useAuthorization from "../../contexts/useAuthorization";
import { useCountdown } from "../../services/CountDown";
import { relayContractCall, isUserRejection } from "../../services/relay.service";
import { getContract, getMetaMaskSigner } from "../../services/contract.service";
import { checkTransactionConfirmation } from "../../utils/methods";
import { formatCountdown } from "../../utils/countdown";
import { PopupActionButton, PopupGhostButton, PopupProcessingButton } from "../PopupButtons";
import ChallengeDeedDialog, { DeedChallengeKind } from "./ChallengeDeedDialog";
import {
  ChallengeKind,
  ChallengeState,
  WindowAction,
  WindowKind,
  challengeWindowBlock,
  describeChainError,
  hasChallenged,
  raiseChallenge,
  releaseAfterReVerification,
  withdrawChallenge,
} from "../../services/challenge.service";
import useCovenantReviewState from "../../hooks/useCovenantReviewState";
import {
  CalloutSubText,
  CalloutText,
  ErrorNote,
  Footnote,
  HashLine,
  InfoCallout,
  MetaItem,
  MonoMeta,
  MonoValue,
  SectionLabel,
  figmaButtonSx,
} from "./settlementStyles";
import { CURRENCY_NAME } from "../../utils/constants";
import { SettlementState } from "../../types/LandToken";
import { KycStatus } from "../../types/User";

// Post-auction settlement panel (V9.1 state machine, V10.1 challenge lane). Winning an auction
// no longer transfers the EFT — the sale runs through AUCTION_ENDED → KYC_CONFIRMED →
// DEED_ANCHORED → DEED_ATTESTED → FINALISED (with a CHALLENGED branch) and is only final at
// FINALISED. This panel renders the state machine off GET /settlement/:tokenId (falling back to
// bank.getSettlement) and shows the viewer's action for the current state: the bound verifier
// anchors the deed (relay), independent verifiers/holders attest it (EcoBank.attestDeed, relay)
// or challenge it (EcoChallenge.raiseChallenge, a DIRECT transaction — EcoChallenge reads raw
// msg.sender), and a drawn checker signs the determination. Everyone else sees progress. Attesting
// is a one-time step at DEED_ANCHORED; once FINALISED the sale is over and the panel steps aside —
// what happens to the live covenant after that is CovenantChallengePanel's.

const STEPS = ["Auction ended", "KYC confirmed", "Deed anchored", "Deed attested", "Finalised"];

// The settlement steps a backend worker sends on its own (1-minute tick) — the user neither
// triggers these nor waits on them synchronously. Keys are the server's SettlementTxAction
// enum *values* (snake_case), which is what pendingTxAction carries — not the function names.
const PENDING_TX_COPY: Record<string, string> = {
  confirm_kyc: "Confirming KYC on-chain",
  finalise: "Finalising the settlement",
  cancel_stale: "Cancelling the settlement and refunding the patron",
  refund_and_relist: "Refunding the patron and relisting the EFT",
};

const STEP_BY_STATE: Partial<Record<SettlementState, number>> = {
  auction_ended: 1,
  kyc_confirmed: 2,
  deed_anchored: 3,
  deed_attested: 4,
  finalised: 5,
};

const shortAddress = (addr: string) => `${addr.slice(0, 6)}…${addr.slice(-4)}`;

// The redesigned callout bolds the lead clause and sets the countdown in mono — "KYC has
// cleared: anchor the signed deed within 1D 23H 59M 47S". Only the two states that carry a
// countdown are drawn that way, so the markup is applied only when there is one. The copy
// itself is untouched: this marks up the string the switch below already produced, splitting
// on the em dash the sentences already use.
const StatusSentence = ({ copy, countdown }: { copy: string; countdown: string }) => {
  if (!countdown) {
    return <CalloutText>{copy}</CalloutText>;
  }
  const dash = copy.indexOf("—");
  const lead = dash > 0 ? copy.slice(0, dash).trim() : null;
  const rest = (dash > 0 ? copy.slice(dash + 1) : copy).trim();
  const at = rest.indexOf(countdown);

  return (
    <CalloutText>
      {lead && <strong>{lead}: </strong>}
      {at >= 0 ? (
        <>
          {rest.slice(0, at)}
          <MonoValue>{countdown}</MonoValue>
          {rest.slice(at + countdown.length)}
        </>
      ) : (
        rest
      )}
    </CalloutText>
  );
};

export interface SettlementPanelProps {
  // On-chain token id (the Bank's settlement key — etNft.tokenId).
  tokenId: number | string;
  // Connected wallet, for role-aware rendering. Authorization is ultimately on-chain —
  // these only decide what to *show*.
  viewerAddress?: string | null;
  // User.role from the backend ("trust" | "admin" | ...). Attestors are independent
  // verifiers or holders — there is no separate attestor role. For attesting this is secondary
  // to the relay's own capability answer, and only the fallback when /relay/capabilities is
  // unreachable; for judging a challenge it is a hard requirement ("admin") on top of it.
  viewerRole?: string | null;
  // The easement's bound verifier — the only wallet that can anchor the deed, and
  // excluded from attesting its own EFT.
  boundVerifier?: string | null;
  // Pre-fetched settlement view. Supplying it means the owner has already resolved the read
  // (LandPage folds it into its page payload so the panel is there on the first frame rather
  // than dropping in a round-trip later and shoving the cards below it down), and the panel
  // skips its own fetch entirely. Leave it `undefined` to have the panel fetch for itself;
  // `null` is a real value meaning "the read failed / there is nothing to settle".
  settlement?: Settlement | null;
  // Parent refetch (REST data) after a settlement action lands on-chain. When `settlement` is
  // injected this is also how the panel's own copy gets refreshed, so it must re-resolve it.
  onChanged?: () => void;
  sx?: SxProps<Theme>;
}

const SettlementPanel = (props: SettlementPanelProps) => {
  const { tokenId, viewerAddress, viewerRole, boundVerifier, onChanged, sx } = props;
  const navigate = useNavigate();
  const { user } = useAuthorization();
  // Hooks can't be called conditionally, so the self-fetch is disabled by passing a null
  // tokenId rather than by skipping the hook.
  const injected = props.settlement !== undefined;
  const own = useSettlement(injected ? null : tokenId);
  const settlement = injected ? props.settlement : own.settlement;
  // Injected data is resolved before it ever reaches us, so there is no in-flight state.
  const loading = injected ? false : own.loading;
  // Injected: the owner re-resolves its own copy off onChanged, so there is nothing to refresh here.
  const refresh = injected ? () => Promise.resolve() : own.refresh;
  const [anchorOpen, setAnchorOpen] = useState(false);
  const [judgeOpen, setJudgeOpen] = useState(false);
  // An upheld verdict leaves the chain in CHALLENGED (V10.1: the settlement is HELD until the
  // covenant is reseated and re-verified, then released — it is not refunded), and the backend
  // mirror does not yet reflect EcoChallenge determinations. Without this the refetch that
  // follows the verdict re-renders the "Review challenge" button as though nothing happened.
  const [upheldLocally, setUpheldLocally] = useState(false);
  const [attestBusy, setAttestBusy] = useState(false);
  const [challengeOpen, setChallengeOpen] = useState(false);
  const [challengeKind, setChallengeKind] = useState<DeedChallengeKind | null>(null);
  const [challengeReason, setChallengeReason] = useState("");
  const [challengeBusy, setChallengeBusy] = useState(false);
  const [finaliseBusy, setFinaliseBusy] = useState(false);
  const [withdrawBusy, setWithdrawBusy] = useState(false);
  const [releaseBusy, setReleaseBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  // One-challenge-per-wallet-per-token, ever (EcoChallenge.hasChallenged). Checked up front so the
  // button explains rather than the dialog reverting AlreadyChallenged after the evidence is typed.
  const [viewerHasChallenged, setViewerHasChallenged] = useState(false);

  // The deed review window and any standing challenge, from chain. The window decides whether a
  // challenge would HOLD the settlement (only while `closesAt` has not passed — see handleChallenge);
  // the challenge decides whether the challenger may still withdraw.
  const { state: review, refresh: refreshReview } = useCovenantReviewState(tokenId);

  const state = settlement?.state ?? "none";
  const viewer = viewerAddress?.toLowerCase() ?? null;
  const isWinner = !!viewer && settlement?.winner?.toLowerCase() === viewer;
  const isBoundVerifier = !!viewer && boundVerifier?.toLowerCase() === viewer;
  // Ask the relay which functions this wallet may actually send, rather than inferring it from
  // User.role — the org graph is what the relay checks, and it knows about suspension, freezes
  // and dismissal that the role string doesn't. Degrades to the old role guess if the endpoint
  // is unreachable, so an outage doesn't hide every action.
  const { can: canRelay, unavailable: capabilitiesUnavailable } = useRelayCapabilities(viewerAddress);
  const roleLooksLikeAttestor = viewerRole === "trust" || viewerRole === "admin";
  // Frontend-side gate only: the contract additionally rejects attestors from the same
  // org as the bound verifier or the admitting holder.
  const canAttest =
    !!viewer && !isBoundVerifier && (capabilitiesUnavailable ? roleLooksLikeAttestor : canRelay("attestDeed"));
  // V10.1: adjudication is a drawn checker (deed stage) signing an EIP-712 verdict that anyone
  // may submit — EcoChallenge.recordOutcome has no role gate; the signature is the authority.
  // Seats are drawn from the country's Trust Admins and the GTA registry, so "admin" is the role
  // that can ever hold one. The relay's capability list no longer applies (this is a direct
  // transaction), and the dialog itself says whether the connected wallet holds the seat.
  const canJudge = !!viewer && viewerRole === "admin";
  // Chain first (the last DETERMINED challenge on this token upheld and is still holding the
  // settlement), then the local flag set the moment the verdict mined. The backend's "challenge
  // upheld at" stamp is deliberately NOT a signal: it is legacy, served but never written since
  // V10 (the GTA judge lane that stamped it is gone), so it would only ever be null.
  const challengeUpheld = !!review?.heldSettlement || upheldLocally;
  // The held settlement releases only through EcoChallenge.releaseAfterReVerification, once
  // verificationsPerformed has advanced past the count recorded at the upheld finding
  // (EcoChallenge.sol:962-984 — NotReVerified otherwise). A backend job normally sends it; this is
  // the manual fallback, offered to any connected wallet once the chain says it would succeed.
  const releaseTarget = review?.heldSettlement && review.reVerified ? review.lastDetermined : null;
  const canRelease = state === "challenged" && !!viewer && !!releaseTarget;
  // An upheld FIELD finding moved the seat and the dismissed verifier is still seated: the
  // patron's money waits on the Trust Admin's dismiss → reseat, then the new verifier's
  // re-verification. Said in the copy so "held" is never a dead end.
  const reseatPending = !!review?.reseatRequired;

  // A challenge holds the settlement only if it takes the OPEN deed window (EcoChallenge.raiseChallenge:
  // `w.kind != NONE && now <= closesAt && action != LAPSED`). The deed window (14 d prod / 4 h on the
  // accelerated testnet) is opened at confirmActivation and is normally longer than the attest
  // withhold, but once it has closed a "deed challenge" would be recorded without holding the money —
  // so the button hides itself and the dialog copy stops promising a hold. Issue #628 adds the
  // other half: a window another challenge already holds is refused WindowAlreadyTaken in V10.1.2,
  // so CHALLENGED hides it too (challengeWindowBlock), and the clock is the chain's.
  const deedWindow = review?.window ?? null;
  const deedWindowOpen =
    !!deedWindow &&
    deedWindow.kind === WindowKind.DEED &&
    challengeWindowBlock(deedWindow, Date.now() + (review?.chainClockOffsetMs ?? 0)) === null;
  // §10: the challenger may withdraw before a panel is seated. No flag; the held settlement is
  // released exactly as a dismissal would release it.
  const liveChallenge = review?.challenge ?? null;
  const canWithdraw =
    !!viewer &&
    !!liveChallenge &&
    liveChallenge.challenger.toLowerCase() === viewer &&
    (liveChallenge.state === ChallengeState.OPEN || liveChallenge.state === ChallengeState.RESPONDED);

  useEffect(() => {
    if (!viewer || !canAttest) {
      setViewerHasChallenged(false);
      return;
    }
    let cancelled = false;
    hasChallenged(tokenId, viewer)
      .then((v) => {
        if (!cancelled) {
          setViewerHasChallenged(v);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setViewerHasChallenged(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [tokenId, viewer, canAttest]);

  // Has the winner cleared KYC? user.kycStatus is the right source and the only one available:
  // it is the *same column* SettlementWorker.confirmKycSettlements selects on
  // (`users.kycStatus = KYC_VERIFIED`), so it is exactly the gate that decides whether the sweep
  // will fire confirmKYC and move this settlement off AUCTION_ENDED.
  //
  // Do NOT substitute EcoBank.kycVerified(winner) for this. That mapping is only a *mirror* — the
  // bridge writes it after the provider clears a wallet so endAuction can pre-open a settlement at
  // KYC_CONFIRMED, and the backend reads it solely as a dedupe check (EcoBankContract.isKycVerified)
  // to avoid paying gas twice. It can read true while the settlement never advances (set
  // out-of-band, or kycStatus later reset by a trust/admin flow), which makes it actively
  // misleading here.
  //
  // The cost of using kycStatus is that it belongs to the *logged-in user*: it answers the question
  // only when the viewer is the winner. No REST surface exposes a patron's KYC state for anybody
  // else — GET /settlement/:tokenId projects `patron` (the address) and nothing about their KYC, and
  // /settlement + /settlement/mine return bare EtNft rows. So a third party genuinely cannot know,
  // and reads the state machine (state + pendingTxAction) instead of being told a guess.
  const winnerKycVerified = isWinner && user?.kycStatus === KycStatus.KYC_VERIFIED;
  const winnerKycPending = isWinner && user?.kycStatus === KycStatus.KYC_PENDING;

  // The activation countdown comes from the server's derived deadlineAt — that window lives in
  // backend config (V9.1: 2 d accelerated, 30 d production) and has no getter.
  //
  // The finalise countdown does NOT, any more. finalise() opens at attestedAt + attestWithholdWindow
  // (EcoBank.finalise), and V10.1.2 moved that window to 4 h on testnet while the backend's config
  // still says 1 h — its deadlineAt would put the Finalise fallback up three hours early, to revert
  // FinaliseWindowNotReached. The window has no getter either, but confirmActivation writes
  // withholdUntil = now + attestWithholdWindow AND opens the deed review window in the same
  // transaction, so withholdUntil − that window's openedAt IS the contract's window. (A dismissed
  // deed challenge re-stamps withholdUntil later; the derived window then reads long, which only
  // makes the fallback appear late — the backend worker finalises on its own regardless.) The
  // server's deadlineAt, then the raw withholdUntil, remain the fallbacks when that cannot be read.
  const activationDeadline = state === "kyc_confirmed" ? settlement?.deadlineAt ?? null : null;
  const deedWindowOpenedAt =
    review?.window?.kind === WindowKind.DEED && review.window.openedAt ? review.window.openedAt.getTime() : null;
  const withholdWindowMs =
    settlement?.withholdUntil && deedWindowOpenedAt !== null
      ? settlement.withholdUntil.getTime() - deedWindowOpenedAt
      : null;
  const finaliseAt =
    settlement?.attestedAt && withholdWindowMs !== null && withholdWindowMs > 0
      ? new Date(settlement.attestedAt.getTime() + withholdWindowMs)
      : null;
  const withholdEnd =
    state === "deed_attested" ? finaliseAt ?? settlement?.deadlineAt ?? settlement?.withholdUntil ?? null : null;
  const activationCountdown = formatCountdown(useCountdown(activationDeadline) as string[]);
  const withholdCountdown = formatCountdown(useCountdown(withholdEnd) as string[]);
  // useCountdown ticks every second, so this flips on its own the moment the window closes.
  const withholdElapsed = !!withholdEnd && withholdEnd.getTime() <= Date.now();

  // A server-signed transaction for this token is already in flight (endAuction, confirmKYC,
  // finalise, cancelStaleSettlement or refundAndRelist — sent by a backend worker on a 1-minute
  // tick). Say "processing" rather than leaving the state looking stalled, and hide the
  // viewer's action so they can't push against a transaction that is already on its way.
  const serverTxPending = !!settlement?.pendingTxAction;

  // Nothing to show before a settlement opens, and nothing left to do once it is final — for
  // everyone, winner and attestor included. The regular sold UI takes over from there.
  if (state === "none" || state === "finalised") {
    return null;
  }

  const settled = async () => {
    await Promise.all([refresh(), refreshReview()]);
    onChanged?.();
  };

  // Permissionless, direct transaction — EcoChallenge reads no sender here. See canRelease.
  const handleRelease = async () => {
    if (!releaseTarget) {
      return;
    }
    setReleaseBusy(true);
    setActionError(null);
    try {
      await releaseAfterReVerification(tokenId, releaseTarget.challengeId);
      setUpheldLocally(false);
      await settled();
    } catch (e: any) {
      if (!isUserRejection(e)) {
        setActionError(describeChainError(e));
      }
    } finally {
      setReleaseBusy(false);
    }
  };

  const handleWithdraw = async () => {
    if (!liveChallenge) {
      return;
    }
    setWithdrawBusy(true);
    setActionError(null);
    try {
      await withdrawChallenge(liveChallenge.challengeId);
      await settled();
    } catch (e: any) {
      if (!isUserRejection(e)) {
        setActionError(describeChainError(e));
      }
    } finally {
      setWithdrawBusy(false);
    }
  };

  const handleAttest = async () => {
    setAttestBusy(true);
    setActionError(null);
    try {
      // Gasless lane: one EIP-712 signature → backend /relay → resolved only once
      // DeedAttested is observed on-chain.
      await relayContractCall("ecofutures-bank", "attestDeed", [tokenId], "DeedAttested", "Attest deed");
      await settled();
    } catch (e: any) {
      if (!isUserRejection(e)) {
        setActionError(e?.message || "Failed to attest the deed");
      }
    } finally {
      setAttestBusy(false);
    }
  };

  // V10.1: EcoChallenge.raiseChallenge(tokenId, kind, evidence) as a DIRECT wallet transaction.
  // Not the relay — EcoChallenge reads raw msg.sender, so a relayed call would arrive as the
  // forwarder and fail _requireIndependent. The deed window is open at DEED_ANCHORED /
  // DEED_ATTESTED, so the challenge takes it (superseding an attestation already made in it) and
  // holds the settlement; EcoBank still emits DeedChallenged with the real challenger, which is
  // what the backend's settlement mirror keys on.
  const handleChallenge = async () => {
    if (!challengeReason.trim() || !challengeKind) {
      setActionError("Choose the kind of challenge and give your evidence");
      return;
    }
    setChallengeBusy(true);
    setActionError(null);
    try {
      await raiseChallenge(
        tokenId,
        challengeKind === "field" ? ChallengeKind.FIELD : ChallengeKind.EVIDENCE,
        challengeReason.trim()
      );
      setChallengeOpen(false);
      setChallengeReason("");
      setChallengeKind(null);
      await settled();
    } catch (e: any) {
      if (!isUserRejection(e)) {
        setActionError(describeChainError(e));
      }
    } finally {
      setChallengeBusy(false);
    }
  };

  // Manual fallback for the last step. finalise() is permissionless — no role, no relay lane —
  // so it goes out as an ordinary wallet transaction and the sender pays gas (it is deliberately
  // absent from the relay's allowlist). A backend worker sends it on its own 1-minute tick once
  // the withhold window closes; this button only exists for when that worker is down or behind.
  const handleFinalise = async () => {
    setFinaliseBusy(true);
    setActionError(null);
    try {
      const signer = await getMetaMaskSigner();
      const bank = getContract("ecofutures-bank", signer);
      if (!bank) {
        throw new Error("Bank contract not found");
      }
      const tx = await bank.finalise(tokenId);
      await checkTransactionConfirmation(tx.hash);
      await settled();
    } catch (e: any) {
      if (!isUserRejection(e)) {
        // The worker racing us to it is the common case, and it is a success, not a failure.
        setActionError(e?.reason || e?.message || "Failed to finalise the settlement");
      }
    } finally {
      setFinaliseBusy(false);
    }
  };

  const escrow = settlement?.escrowAmount ? `${formatUnits(settlement.escrowAmount, 18)} ${CURRENCY_NAME}` : null;
  const chainStep = state === "challenged" ? (settlement?.attestedAt ? 4 : 3) : STEP_BY_STATE[state] ?? 0;
  // A winner the registry already lists has done everything the "KYC confirmed" step asks of them,
  // so tick it off rather than leaving it pending until the sweep fires confirmKYC. winnerKycVerified
  // is already isWinner-scoped, so this only ever advances the winner's own view.
  const activeStep = state === "auction_ended" && winnerKycVerified ? 2 : chainStep;
  // An upheld challenge HOLDS the settlement (V10.1): it stays CHALLENGED until the covenant is
  // reseated and re-verified, and only then releases via releaseAfterReVerification. It is not
  // refunded by the verdict, so relabel the last step to what is actually being waited on.
  const steps = challengeUpheld ? [...STEPS.slice(0, 4), "Re-verification"] : STEPS;

  // Per-state copy: the viewer's line first, generic progress for everyone else.
  let statusCopy = "";
  switch (state) {
    case "auction_ended":
      if (isWinner && winnerKycVerified) {
        statusCopy =
          `Your identity is verified — ${escrow ? `your ${escrow} is` : "your funds are"} held in escrow` +
          " while the settlement's KYC gate clears. The EFT transfers once settlement finalises.";
      } else if (isWinner && winnerKycPending) {
        statusCopy =
          `Your identity verification is under review — ${escrow ? `your ${escrow} is` : "your funds are"} held` +
          " in escrow. Settlement continues once it clears.";
      } else if (isWinner) {
        statusCopy =
          `You won this auction — ${escrow ? `your ${escrow} is` : "your funds are"} held in escrow` +
          " while KYC completes. The EFT transfers once settlement finalises.";
      } else {
        // No source tells a third party where the winner's KYC stands (see winnerKycVerified), so
        // this stays deliberately non-committal — pendingTxAction above is the one real signal that
        // the confirmKYC transaction is already on its way.
        statusCopy = "Sold — settling. The winner is recorded and KYC is in progress.";
      }
      break;
    case "kyc_confirmed":
      statusCopy = isBoundVerifier
        ? `KYC has cleared — anchor the signed deed${
            activationCountdown ? ` within ${activationCountdown}` : " within the activation window"
          }.`
        : isWinner
        ? "Your KYC has cleared — the verifier is anchoring the deed."
        : "KYC cleared — awaiting the deed.";
      break;
    case "deed_anchored":
      statusCopy = canAttest
        ? "The deed is anchored and awaits attestation by an independent verifier or holder."
        : "The deed is anchored — awaiting independent attestation.";
      break;
    case "deed_attested":
      statusCopy = withholdElapsed
        ? "The withhold window has closed — the settlement finalises automatically within a minute."
        : "The deed is attested. The sale finalises after the withhold window" +
          `${withholdCountdown ? ` — ${withholdCountdown} remaining` : ""}.`;
      break;
    case "challenged":
      // An upheld verdict leaves the settlement in CHALLENGED on-chain (V10.1: held, not
      // refunded) — the patron's escrow is released once the covenant is reseated and re-verified.
      statusCopy = challengeUpheld
        ? review?.reVerified
          ? "The challenge was upheld and the covenant has since been re-verified — the held settlement can now be released."
          : reseatPending
          ? "The challenge was upheld — the settlement is held until the Trust Admin dismisses the seated verifier and reseats " +
            "the covenant on the challenger, whose re-verification then releases it."
          : "The challenge was upheld — the settlement is held until the covenant is re-verified, then released. It is not refunded."
        : canJudge
        ? "The deed has been challenged and settlement is held. Review the challenge and record the determination."
        : "The deed has been challenged and is under review. Settlement is held until a determination is recorded.";
      break;
  }

  // Which of the callout's tones this state gets. A transaction already on its way is the most
  // useful thing to show — it is what the viewer is waiting on — so it outranks the challenge
  // branch, whose own narrative statusCopy already carries.
  const calloutTone = serverTxPending ? "progress" : state === "challenged" ? "warning" : "info";
  const calloutIcon = serverTxPending ? (
    // Heavier than the default stroke: a thin arc on the pale disc all but disappears.
    <CircularProgress size={30} thickness={4.5} sx={{ color: "#1B5E9E" }} />
  ) : state === "challenged" ? (
    <GavelOutlinedIcon sx={{ fontSize: 34, color: "#B76E00" }} />
  ) : undefined;

  // The mono row under the status sentence. Winner and escrow are the drawn pair; the deed date
  // has no place in the Figma but is real settlement data, so it rides along here rather than
  // being dropped when the old meta lines went away.
  const metaItems: MetaItem[] = [];
  if (settlement?.winner) {
    metaItems.push({ label: "Sold to", value: shortAddress(settlement.winner) });
    if (escrow) {
      metaItems.push({ label: "Escrow", value: escrow });
    }
  }
  if (settlement?.deedDate) {
    metaItems.push({ label: "Deed date", value: settlement.deedDate.toLocaleDateString() });
  }

  return (
    <Box
      sx={{
        backgroundColor: "white",
        borderRadius: "20px",
        border: "none",
        padding: { xs: "16px", sm: "24px" },
        ...sx,
      }}
    >
      <SectionLabel>Settlement{loading ? " …" : ""}</SectionLabel>

      <ProgressStepper
        steps={steps}
        activeStep={activeStep}
        sx={{
          // Five two-word labels is the longest tracker in the app, and on a phone the shared
          // 8px step gutters (5 × 16px) push "Finalised" past the card edge. Reclaim the gutters,
          // and a point of type on the narrowest handsets, so the row fits instead of clipping.
          // Scoped here rather than in ProgressStepper: the Scan / verification / KYC steppers
          // are shorter and fit as they are.
          "@media (max-width: 560px)": {
            "& .MuiStep-root": { paddingLeft: "2px", paddingRight: "2px" },
          },
          // Doubled class on purpose: StepLabel sets its own phone font-size through an sx of the
          // same specificity, and being rendered later it would otherwise win.
          "@media (max-width: 380px)": {
            "& .MuiStepLabel-label.MuiStepLabel-label": { fontSize: "10px" },
          },
        }}
      />

      {/* The drawn rhythm: the callout sits flush under the stepper, everything below it on a
          14px beat. Margins per block would have to special-case which block happens to be
          first, since most of them are conditional. */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: "14px" }}>
        {/* One callout, as drawn. The challenge and server-transaction states aren't drawn, so
            rather than stacking a banner each they change this block's tone and glyph: statusCopy
            already says the challenge was raised or upheld, and a pending server transaction adds
            only its "how long" line underneath. */}
        <InfoCallout tone={calloutTone} icon={calloutIcon}>
          <StatusSentence copy={statusCopy} countdown={activationCountdown || withholdCountdown} />
          {serverTxPending && (
            <CalloutSubText>
              {PENDING_TX_COPY[settlement?.pendingTxAction ?? ""] ?? "Processing this settlement step"} — this can take
              up to a minute.
            </CalloutSubText>
          )}
          {metaItems.length > 0 && <MonoMeta items={metaItems} />}
        </InfoCallout>

        {settlement?.deedHash && <HashLine label="Deed hash" value={settlement.deedHash} />}

        {/* While the challenge dialog is open it shows the error itself, so don't double it up
          behind the modal. */}
        {actionError && !challengeOpen && <ErrorNote>{actionError}</ErrorNote>}

        {/* Winner: complete identity verification so their KYC gate can clear and the
          settlement can advance. Opens the dedicated Confirm-KYC page. Hidden once the
          registry already lists them — there is nothing left for them to submit, the
          settlement is just waiting on its on-chain transition. While PENDING the page
          still has something to show (the merged KYC-status step), so the CTA stays. */}
        {state === "auction_ended" && isWinner && !winnerKycVerified && !serverTxPending && (
          <PopupActionButton
            onClick={() => navigate(AppRoutes.ConfirmKyc.path)}
            variant="contained"
            fullWidth
            sx={figmaButtonSx}
          >
            {winnerKycPending ? "View KYC status" : "Confirm KYC"}
          </PopupActionButton>
        )}

        {/* Bound verifier: anchor the deed while the activation window is open. */}
        {state === "kyc_confirmed" && isBoundVerifier && !serverTxPending && (
          <PopupActionButton onClick={() => setAnchorOpen(true)} variant="contained" fullWidth sx={figmaButtonSx}>
            Anchor deed — no gas needed
          </PopupActionButton>
        )}

        {/* Trust Admins / GTAs: the drawn checker signs, anyone submits. Hidden once a verdict is
          recorded locally — an upheld challenge stays in CHALLENGED until re-verification, and
          recording it twice would just revert InvalidChallengeState. */}
        {state === "challenged" && canJudge && !challengeUpheld && !serverTxPending && (
          <PopupActionButton onClick={() => setJudgeOpen(true)} variant="contained" fullWidth sx={figmaButtonSx}>
            Review challenge
          </PopupActionButton>
        )}

        {/* Independent attestors: attest at DEED_ANCHORED; challenge until finalised. */}
        {canAttest && !serverTxPending && (state === "deed_anchored" || state === "deed_attested") && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
            {state === "deed_anchored" &&
              (attestBusy ? (
                <PopupProcessingButton disabled variant="contained" fullWidth sx={figmaButtonSx}>
                  <CircularProgress size={20} sx={{ color: "white" }} />
                </PopupProcessingButton>
              ) : (
                <PopupActionButton
                  onClick={handleAttest}
                  disabled={challengeBusy}
                  variant="contained"
                  fullWidth
                  sx={figmaButtonSx}
                >
                  Attest deed — no gas needed
                </PopupActionButton>
              ))}
            {/* Drawn as plain black text rather than the red danger treatment it used to carry.
                Only while the deed window is open — outside it the challenge would not hold the
                settlement (see deedWindowOpen) — and only for a wallet that has not already
                challenged this token. */}
            {deedWindowOpen && (
              <PopupGhostButton
                onClick={() => {
                  setChallengeOpen(true);
                  setActionError(null);
                }}
                disabled={attestBusy || challengeBusy || viewerHasChallenged}
                fullWidth
              >
                {viewerHasChallenged ? "You have already challenged this covenant" : "Challenge this deed"}
              </PopupGhostButton>
            )}
            {!deedWindowOpen && review && (
              <Footnote>
                {deedWindow?.kind === WindowKind.DEED && deedWindow.action === WindowAction.CHALLENGED
                  ? "This deed has already been challenged. Only one challenge may hold a deed review window."
                  : "The deed review window is closed, so this deed can no longer be challenged."}
              </Footnote>
            )}
          </Box>
        )}

        {/* The challenger: withdraw before the checker is drawn. Releases the held settlement. */}
        {state === "challenged" && canWithdraw && !serverTxPending && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
            {withdrawBusy ? (
              <PopupProcessingButton disabled variant="contained" fullWidth sx={figmaButtonSx}>
                <CircularProgress size={20} sx={{ color: "white" }} />
              </PopupProcessingButton>
            ) : (
              <PopupGhostButton onClick={handleWithdraw} fullWidth>
                Withdraw your challenge
              </PopupGhostButton>
            )}
            <Footnote>
              Possible until the checker is drawn. Withdrawing releases the settlement and carries no flag. This is an
              on-chain transaction — your wallet pays the gas.
            </Footnote>
          </Box>
        )}

        {/* Anyone: release the held settlement once the covenant has been re-verified past the
          upheld finding. A backend job normally sends this; the button is the fallback for when it
          is down or behind — the same reason the finalise button below exists. */}
        {canRelease && !serverTxPending && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
            {releaseBusy ? (
              <PopupProcessingButton disabled variant="contained" fullWidth sx={figmaButtonSx}>
                <CircularProgress size={20} sx={{ color: "white" }} />
              </PopupProcessingButton>
            ) : (
              <PopupActionButton onClick={handleRelease} variant="contained" fullWidth sx={figmaButtonSx}>
                Release the held settlement
              </PopupActionButton>
            )}
            <Footnote>
              The covenant has been re-verified since the upheld finding. Only needed if the backend has not released it
              on its own. This one is an on-chain transaction — your wallet pays the gas.
            </Footnote>
          </Box>
        )}

        {/* Fallback only: the backend worker finalises on its own once the window closes, so this
          stays hidden until it is genuinely overdue. Permissionless, so it is offered to any
          connected wallet — but unlike every other action here it is a real transaction the
          sender pays gas for, which the copy has to say. */}
        {state === "deed_attested" && withholdElapsed && !!viewer && !serverTxPending && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
            {finaliseBusy ? (
              <PopupProcessingButton disabled variant="contained" fullWidth sx={figmaButtonSx}>
                <CircularProgress size={20} sx={{ color: "white" }} />
              </PopupProcessingButton>
            ) : (
              <PopupActionButton onClick={handleFinalise} variant="contained" fullWidth sx={figmaButtonSx}>
                Finalise settlement
              </PopupActionButton>
            )}
            <Footnote>
              Only needed if it hasn&apos;t finalised on its own. This one is an on-chain transaction — your wallet pays
              the gas.
            </Footnote>
          </Box>
        )}
      </Box>

      <AnchorDeedDialog
        open={anchorOpen}
        onClose={() => setAnchorOpen(false)}
        tokenId={tokenId}
        kycConfirmedAt={settlement?.kycConfirmedAt}
        onAnchored={settled}
      />

      <JudgeChallengeDialog
        open={judgeOpen}
        onClose={() => setJudgeOpen(false)}
        tokenId={tokenId}
        viewerAddress={viewerAddress}
        onJudged={(upheld) => {
          if (upheld) {
            setUpheldLocally(true);
          }
          settled();
        }}
      />

      <ChallengeDeedDialog
        open={challengeOpen}
        onClose={() => setChallengeOpen(false)}
        holdsSettlement={deedWindowOpen}
        kind={challengeKind}
        onKindChange={setChallengeKind}
        reason={challengeReason}
        onReasonChange={setChallengeReason}
        onSubmit={handleChallenge}
        busy={challengeBusy}
        error={actionError}
      />
    </Box>
  );
};

export default SettlementPanel;
